import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ContentLoader from "@/authentication/ContentLoader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Building,
  Search,
  Filter,
  Edit,
  Trash2,
  Users,
  MapPin,
  UserPlus,
  RefreshCw,
} from "lucide-react";
import DormRegistrationForm from "@/components/forms/DormRegistrationForm";
import DormEditForm from "@/components/forms/DormEditForm";
import DormOccupantsView from "@/components/DormOccupantsView";
import StudentsWithoutDormView from "@/components/StudentsWithoutDormView";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

import { db, auth } from "../firebase/config";
import { collection, onSnapshot, doc, deleteDoc, getDoc, query, where, getDocs, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface Dorm {
  id: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  schoolID: string;
  dormUid: string;
  submittedBy: string;
  createdAt: Date;
}

interface DormInput {
  id?: string;
  name: string;
  location: string;
  capacity: number;
  occupied: number;
  available: number;
  patronTeacherId?: string | null;
  dormUid?: string;
  submittedBy: string;
  schoolID: string;
  createdAt: Date | { seconds: number; nanoseconds: number };
}

type ActiveView = "list" | "register" | "edit" | "occupants" | "without-dorm";

const DormsManagement = ({ onBack }: { onBack?: () => void }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeView, setActiveView] = useState<ActiveView>("list");
  const [selectedDorm, setSelectedDorm] = useState<Dorm | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dorms, setDorms] = useState<Dorm[]>([]);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clean up students assigned to deleted dorms
  const cleanupDeletedDormAssignments = async (schoolId: string, existingDormNames: string[]) => {
    try {
      const studentsRef = collection(db, `schools/${schoolId}/students`);
      const studentsSnapshot = await getDocs(studentsRef);
      
      const cleanupPromises = studentsSnapshot.docs
        .filter(doc => {
          const studentData = doc.data();
          return studentData.dormitory && !existingDormNames.includes(studentData.dormitory);
        })
        .map(doc => 
          updateDoc(doc.ref, {
            dormitory: null,
            bedNumber: null,
          })
        );

      if (cleanupPromises.length > 0) {
        await Promise.all(cleanupPromises);
        if (process.env.NODE_ENV !== "production") {
          console.log(`Cleaned up ${cleanupPromises.length} students with deleted dorm assignments`);
        }
      }
    } catch (error: any) {
      console.error("Error cleaning up deleted dorm assignments:", error);
      // Don't show error to user as this is a background cleanup operation
    }
  };

  // Handle authentication, claim verification, and initial data fetch
  useEffect(() => {
    let unsubscribeAuth: () => void;
    let unsubscribeDorms: () => void;

    const authInstance = auth;
    unsubscribeAuth = onAuthStateChanged(authInstance, async (user) => {
      if (!user) {
        setLoading(false);
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("DormsManagement: No user found, deferring to App.tsx routing");
        return;
      }

      try {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult(true);
        if (!idTokenResult) throw new Error("Failed to retrieve user token claims");

        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        if (process.env.NODE_ENV !== "production") {
          console.log("ID Token Result claims:", JSON.stringify(idTokenResult.claims, null, 2));
        }

        let selectedAffiliation = defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find(aff => aff.role === "admin" && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          await new Promise(resolve => setTimeout(resolve, 10000));
          await user.getIdToken(true);
          const retryIdTokenResult = await user.getIdTokenResult(true);
          const retryAffiliations = (retryIdTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

          selectedAffiliation = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
            ? retryDefaultAff
            : retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== retrySchoolID) {
            throw new Error(`Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(retryDefaultAff)}, schoolID=${retrySchoolID}`);
          }
        }

        const currentSchoolID = selectedAffiliation.schoolID ?? null;
        setSchoolID(currentSchoolID);
        if (currentSchoolID) {
          fetchData(currentSchoolID);
        }
      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        // Check if it's a network error
        const isNetworkError = error?.code === 'auth/network-request-failed' ||
                              error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                              error?.message?.includes('network-request-failed') ||
                              error?.message?.includes('network');
        
        if (isNetworkError) {
          // Don't logout on network errors, just show a warning
          toast({
            title: "Network Error",
            description: "Unable to verify permissions due to network issues. Please check your connection.",
            variant: "destructive",
            duration: 5000,
          });
        } else {
          // Only logout for non-network errors
          toast({
            title: "Missing Permissions",
            description: "You lack the necessary permissions to manage dormitories. Please contact support.",
            variant: "destructive",
            duration: 5000,
          });
          navigate("/login-page");
        }
      }
    });

    const fetchData = (schoolId: string) => {
      setLoading(true);
      const dormsCollectionRef = collection(db, `schools/${schoolId}/dorms`);
      unsubscribeDorms = onSnapshot(dormsCollectionRef, async (snapshot) => {
        try {
          const dormsData: Dorm[] = [];
          const existingDormNames = snapshot.docs.map(doc => doc.data().name);
          
          // Clean up students with deleted dorms
          await cleanupDeletedDormAssignments(schoolId, existingDormNames);
          
          for (const doc of snapshot.docs) {
            const data = doc.data() as DormInput;
            const occupied = await fetchOccupancy(data.name, schoolId);
            dormsData.push({
              id: doc.id,
              name: data.name,
              location: data.location,
              capacity: data.capacity,
              occupied,
              available: data.capacity - occupied,
              patronTeacherId: data.patronTeacherId || null,
              schoolID: data.schoolID,
              dormUid: data.dormUid ?? doc.id,
              submittedBy: data.submittedBy,
              createdAt: data.createdAt instanceof Date
                ? data.createdAt
                : new Date(data.createdAt.seconds * 1000 + data.createdAt.nanoseconds / 1000000),
            });
          }
          setDorms(dormsData.sort((a, b) => a.name.localeCompare(b.name)));
          setError(null);
        } catch (error: any) {
          console.error("Error fetching dorms:", error);
          setError("Failed to fetch dormitories. Please retry.");
          toast({
            title: "Error Loading Dorms",
            description: `Failed to fetch dormitories: ${error.message || "An unexpected error occurred."}`,
            variant: "destructive",
            duration: 5000,
          });
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error("Firestore listener error:", error);
        setError("Failed to fetch dormitories. Please retry.");
        setLoading(false);
        toast({
          title: "Error Loading Dorms",
          description: `Failed to fetch dormitories: ${error.message || "An unexpected error occurred."}`,
          variant: "destructive",
          duration: 5000,
        });
      });
    };

    return () => {
      unsubscribeAuth?.();
      unsubscribeDorms?.();
    };
  }, [navigate, toast]);

  const fetchOccupancy = useCallback(async (dormName: string, schoolId: string): Promise<number> => {
    try {
      const studentsRef = collection(db, `schools/${schoolId}/students`);
      const q = query(studentsRef, where("dormitory", "==", dormName.trim()));
      const snapshot = await getDocs(q);
      const count = snapshot.size;
      if (process.env.NODE_ENV !== "production") {
        console.log(`Occupancy for ${dormName}: ${count} students`, {
          students: snapshot.docs.map(doc => ({
            id: doc.id,
            dormitory: doc.data().dormitory,
          })),
        });
      }
      return count;
    } catch (error: any) {
      console.error(`Error fetching occupancy for ${dormName}:`, error);
      toast({
        title: "Error Fetching Occupancy",
        description: `Failed to fetch occupancy for ${dormName}: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
      return 0;
    }
  }, [toast]);

  const handleRegisterDorm = () => setActiveView("register");
  const handleEditDorm = (dorm: Dorm) => {
    setSelectedDorm(dorm);
    setActiveView("edit");
  };
  const handleViewOccupants = (dorm: Dorm) => {
    setSelectedDorm(dorm);
    setActiveView("occupants");
  };
  const handleViewStudentsWithoutDorm = () => setActiveView("without-dorm");
  const handleDeleteDorm = async (dormId: string) => {
    if (!schoolID) {
      toast({
        title: "Authentication Error",
        description: "School ID missing. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "User is not authenticated. Please log in again.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      const dormDoc = await getDoc(doc(db, `schools/${schoolID}/dorms`, dormId));
      if (dormDoc.exists()) {
        const dormName = dormDoc.data().name;
        const studentsRef = collection(db, `schools/${schoolID}/students`);
        const q = query(studentsRef, where("dormitory", "==", dormName.trim()));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          toast({
            title: "Cannot Delete Dorm",
            description: "This dormitory has assigned students. Reassign or remove students before deleting.",
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
      }

      await deleteDoc(doc(db, `schools/${schoolID}/dorms`, dormId));
      toast({
        title: "Dorm Deleted",
        description: "Dormitory has been deleted successfully.",
        duration: 3000,
      });
    } catch (error: any) {
      console.error("Error deleting dorm:", error);
      toast({
        title: "Error Deleting Dorm",
        description: `Failed to delete dormitory: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleSaveDorm = (updatedDorm: DormInput) => {
    try {
      if (!updatedDorm.name || !updatedDorm.location || !schoolID) {
        throw new Error("Missing required fields: name, location, or schoolID");
      }

      setDorms((prev) => {
        const exists = prev.some((dorm) => dorm.dormUid === updatedDorm.dormUid);
        if (exists) {
          return prev
            .map((dorm) =>
              dorm.dormUid === updatedDorm.dormUid
                ? {
                    ...dorm,
                    ...updatedDorm,
                    id: updatedDorm.id || dorm.id,
                    createdAt: updatedDorm.createdAt instanceof Date
                      ? updatedDorm.createdAt
                      : new Date(updatedDorm.createdAt.seconds * 1000 + updatedDorm.createdAt.nanoseconds / 1000000),
                  }
                : dorm
            )
            .sort((a, b) => a.name.localeCompare(b.name));
        }
        const newDorm: Dorm = {
          id: updatedDorm.id || updatedDorm.dormUid || '',
          ...updatedDorm,
          dormUid: updatedDorm.dormUid || '',
          createdAt: updatedDorm.createdAt instanceof Date
            ? updatedDorm.createdAt
            : new Date(updatedDorm.createdAt.seconds * 1000 + updatedDorm.createdAt.nanoseconds / 1000000),
        };
        return [...prev, newDorm].sort((a, b) => a.name.localeCompare(b.name));
      });

      toast({
        title: updatedDorm.id ? "Dorm Updated" : "Dorm Registered",
        description: `${updatedDorm.name} has been ${updatedDorm.id ? "updated" : "registered"} successfully.`,
        duration: 3000,
      });

      setActiveView("list");
      setSelectedDorm(null);
    } catch (error) {
      console.error("Error processing dorm:", error);
      toast({
        title: "Error Processing Dorm",
        description: `Failed to process dormitory: ${error instanceof Error ? error.message : "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleBack = () => {
    setActiveView("list");
    setSelectedDorm(null);
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setDorms([]);
    if (schoolID) fetchData(schoolID);
  };

  const getOccupancyColor = (occupied: number, capacity: number) => {
    const percentage = (occupied / capacity) * 100;
    return percentage >= 90 ? "text-red-600" : percentage >= 70 ? "text-yellow-600" : "text-green-600";
  };

  const filteredDorms = dorms.filter((dorm) =>
    dorm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dorm.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (activeView === "register") return <DormRegistrationForm onBack={handleBack} onSave={handleSaveDorm} />;
  if (activeView === "edit" && selectedDorm) return <DormEditForm dorm={selectedDorm} onBack={handleBack} onSave={handleSaveDorm} />;
  if (activeView === "occupants" && selectedDorm) return <DormOccupantsView dorm={selectedDorm} onBack={handleBack} teachers={[]} />;
  if (activeView === "without-dorm") return <StudentsWithoutDormView onBack={handleBack} dorms={dorms} />;

  return (
    <ContentLoader isLoading={loading} message="Loading dormitories...">
      {error ? (
        <div className="flex justify-center items-center flex-col gap-4 py-12">
          <p className="text-gray-900 dark:text-white text-center">{error}</p>
          <Button
            onClick={handleRetry}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-full flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      ) : (
        <div>
          <div className="mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    {onBack && (
                      <Button
                        onClick={onBack}
                        variant="outline"
                        size="sm"
                        className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </Button>
                    )}
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Dormitory Management</h1>
                      <p className="text-gray-600 dark:text-gray-400">Manage school dormitories and student accommodation</p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <Button
                      onClick={handleRegisterDorm}
                      className="flex items-center gap-2 text-sm bg-[#3576DF] hover:bg-[#2a5bbf]"
                    >
                      <Building className="h-4 w-4" />
                      <span className="hidden sm:inline">Register Dormitory</span>
                      <span className="sm:hidden">Register</span>
                    </Button>
                    <Button
                      onClick={handleViewStudentsWithoutDorm}
                      variant="outline"
                      className="flex items-center gap-2 text-sm border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span className="hidden sm:inline">Assign Students</span>
                      <span className="sm:hidden">Assign</span>
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="dormSearch"
                      name="dormSearch"
                      placeholder="Search dormitories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  >
                    <Filter className="h-4 w-4" />
                    Filter
                  </Button>
                </div>
              </div>

              {dorms.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">
                    No dormitories registered. Click "Register Dormitory" to add one.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDorms.map((dorm) => (
                  <Card
                    key={dorm.id}
                    className="hover:shadow-lg transition-shadow border-gray-200 dark:border-slate-800 dark:bg-[#020817]"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white truncate">{dorm.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              {dorm.location}
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="ml-2 flex-shrink-0 text-xs">
                          {dorm.capacity}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Occupancy</span>
                          <span className={`text-sm font-medium ${getOccupancyColor(dorm.occupied, dorm.capacity)}`}>
                            {dorm.occupied}/{dorm.capacity}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min((dorm.occupied / dorm.capacity) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Available: {dorm.available}</span>
                          <span className="text-gray-600 dark:text-gray-400">
                            {Math.round((dorm.occupied / dorm.capacity) * 100)}% full
                          </span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewOccupants(dorm)}
                          className="w-full flex items-center justify-center gap-2 mb-3 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        >
                          <Users className="h-4 w-4" />
                          <span>View Occupants</span>
                        </Button>
                        <div className="flex gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditDorm(dorm)}
                            className="flex-1 flex items-center justify-center gap-1 text-xs sm:text-sm border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                          >
                            <Edit className="h-3 w-3" />
                            <span>Edit</span>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 flex items-center justify-center gap-1 text-xs sm:text-sm text-red-600 hover:text-red-700 border-gray-300 dark:border-gray-600 dark:text-red-500 dark:hover:text-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                                <span>Delete</span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="z-[1000]">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Dormitory</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to permanently delete "{dorm.name}"? This action cannot be undone and will remove all associated data.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => console.log("Delete cancelled")}>
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteDorm(dorm.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete Permanently
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
          </div>
        </div>
      )}
    </ContentLoader>
  );
};

export default DormsManagement;

function fetchData(_schoolID: string) {
  console.warn("fetchData was called, but is a no-op");
}